import type { IApiResponse } from '@/helpers/types';

export const OBSERVER_DIVISION_TYPES = ['state', 'lga', 'ward', 'pollingUnit'] as const;

export type ObserverDivisionType = (typeof OBSERVER_DIVISION_TYPES)[number];
export type ObserverResultType = 'EC8D' | 'EC8C' | 'EC8B' | 'EC8A';

export const OBSERVER_ROUTE_TO_DIVISION: Record<string, ObserverDivisionType> = {
	'state': 'state',
	'local-government': 'lga',
	'ra-ward': 'ward',
	'polling-unit': 'pollingUnit',
};

export const OBSERVER_DIVISION_TO_ROUTE: Record<ObserverDivisionType, string> = {
	state: 'state',
	lga: 'local-government',
	ward: 'ra-ward',
	pollingUnit: 'polling-unit',
};

export const OBSERVER_DIVISION_TO_RESULT: Record<ObserverDivisionType, ObserverResultType> = {
	state: 'EC8D',
	lga: 'EC8C',
	ward: 'EC8B',
	pollingUnit: 'EC8A',
};

export const OBSERVER_DIVISION_LABEL: Record<ObserverDivisionType, string> = {
	state: 'State',
	lga: 'Local Government',
	ward: 'RA / Ward',
	pollingUnit: 'Polling Unit',
};

/**
 * api.core `ObserverApplicationStatus`. Mind the names: `PENDING` (1) is shown as
 * "Application Received" — the server kept the name for compatibility — while the ticket's
 * "Pending" is `WAITLISTED` (8). Render through `observerVolunteerStatus`, never the name.
 *
 * Per entity (election + partner + entity code): approving one applicant sends them the offer
 * (4), makes the earliest other applicant `RESERVED` (7) and everyone else `WAITLISTED` (8).
 * A lapsed offer (5) promotes Reserved → Offer Sent and the earliest Waitlisted → Reserved, but
 * only when something calls `expire-offered-links`. Accepting (6) rejects everyone still in play.
 */
export const ObserverApplicationStatus = {
	PENDING: 1,
	/** Legacy — nothing writes it any more. */
	APPROVED: 2,
	REJECTED: 3,
	OFFER_SENT: 4,
	OFFER_EXPIRED: 5,
	CONFIRMED: 6,
	RESERVED: 7,
	WAITLISTED: 8,
} as const;

export type ObserverApplicationStatusId = (typeof ObserverApplicationStatus)[keyof typeof ObserverApplicationStatus];

export interface IObserverManagementScopeParams {
	electionId: number;
	/**
	 * REQUIRED on every observer-management call — not a Super-Admin-only proxy field.
	 * An Observer Admin's id comes from the JWT, a Super Admin's from the Scope Trail
	 * selection, but the server needs it either way: omit it and `/ElectionObserver/applications`
	 * answers an empty page, which reads as "no applications" rather than "no scope sent".
	 * Build it with `observerApiScope()` so no call site can decide to leave it out.
	 */
	electionObserverId: number;
}

export interface IObserverParentEntity {
	type: ObserverDivisionType;
	code: string;
	name: string;
}

export interface IObserverApplicationSummary {
	id: number;
	applicantId: string;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	electionObserverId: number;
	electionId: number;
	divisionType: ObserverDivisionType;
	resultType: ObserverResultType;
	entityCode: string;
	entityName: string;
	parentPath?: IObserverParentEntity[];
	qualificationScore: number;
	statusId: ObserverApplicationStatusId;
	statusLabel?: string;
	appliedAt: string;
	offerSentAt?: string | null;
	offerExpiresAt?: string | null;
	confirmedAt?: string | null;
	canSendOffer: boolean;
	blockedReason?: string | null;
	gender: string;
	dateOfBirth: string;
	hasSmartPhoneWithInternet: boolean;
	isAvailableAllDay: boolean;
	hasPartyAgentExperience: boolean;
	isNativeSpeaker: boolean;
	hasPowerBank: boolean;
	accountName: string;
	accountNumber: string;
	bankName: string;
	profilePhoto: string;
	faceId: string;
	twitterHandle: string;
	instagramHandle: string;
	facebookHandle: string;
	latitude: number;
	longitude: number;
	kycReviewRequired: boolean;
	dateCreated: string;
	status: ObserverApplicationStatusId;
	reviewedAt: string | null;
	reviewedBy: string | null;
}

export interface IObserverStatusHistory {
	id: number;
	previousStatusId: number | null;
	statusId: number;
	changedAt: string;
	changedBy: string;
	reason?: string | null;
}

/**
 * Only what the detail response adds. The applicant's own fields — including the five
 * qualification answers — are all on the summary above, because the list DTO carries them
 * flat; re-declaring them here as optional made this interface fail to extend its own parent.
 * The grouped shapes below are the detail's own convenience nesting over the flat fields.
 */
export interface IObserverApplicationDetail extends IObserverApplicationSummary {
	statusHistory: IObserverStatusHistory[];
	banking?: { accountName: string; accountNumber: string; bankName: string };
	socialAccounts?: { twitterHandle?: string; instagramHandle?: string; facebookHandle?: string };
	preciseLocation?: { latitude: number; longitude: number };
}

export type ObserverFulfillmentState = 'needs-action' | 'ready' | 'assigned' | 'no-applicants';

/**
 * One row of `GET /ObserverAnalytics` — the assignment ledger, exactly as it arrives.
 *
 * `resultType` is the DIVISION LEVEL (`ec8a` = polling unit … `ec8d` = state), not a
 * result source, and it arrives LOWERCASE while `ObserverResultType` is upper.
 *
 * The row carries ids and a code but NO NAMES, and `userId` identifies a confirmed
 * observer without naming them either.
 */
export interface IObserverLedgerRow {
	id: number;
	electionObserverId: number;
	electionId: number;
	stateId: number;
	lgaId: number;
	wardId: number;
	pollingUnitId: number;
	resultType: string;
	code: string;
	isAssigned: boolean;
	isOfferSent: boolean;
	hasApplicant: boolean;
	applicantCount: number;
	/** 0 when nobody is assigned. Never a name — see `IObserverEntityCoverage.confirmedAt`. */
	userId: number;
	dateAssigned: string | null;
}

/**
 * One entity as the coverage table reads it.
 *
 * Shaped to what the ledger can actually answer. The mock this replaced also carried
 * `parentPath`, `activeOffer`, `applicationReceivedCount`, `reservedCount` and
 * `fulfillmentState` — every one of which had ZERO read sites, so they are gone rather
 * than kept as fields that would now always be undefined.
 */
export interface IObserverEntityCoverage {
	entityId: number | string;
	entityCode: string;
	/**
	 * Resolved client-side from the row's parent id — the ledger has no names. Empty when
	 * the lookup has not resolved (or failed); render the code, never a placeholder name.
	 */
	entityName: string;
	divisionType: ObserverDivisionType;
	resultType: ObserverResultType;
	applicantCount: number;
	hasActiveOffer: boolean;
	isAssigned: boolean;
	/** When the assignment was confirmed. The ledger gives no name to go with it. */
	confirmedAt: string | null;
	/**
	 * The confirmed observer's api.core user id, or 0 when nobody is assigned.
	 *
	 * Carried purely so the reports panel can derive `SHA256("core:{id}")` and recognise which
	 * incidents this partner's observer filed — api.vesr records only that one-way hash, never a
	 * core id or a partner id, so the join can only run in this direction. Kept as the raw
	 * integer because that is exactly what the backend hashes; anything else silently matches
	 * nothing.
	 */
	observerUserId: number;
}

/**
 * One division level's coverage, as the cards read it.
 *
 * Adapted from `GET /ObserverAnalytics/assignment-stats`, which reports only four
 * measured figures. `total` and `awaitingApplicants` are DERIVED here — they are not
 * on the wire, and must not be read as if the server counted them. The names are
 * deliberately not the wire's `*Count` names so that stays visible at the call site.
 */
export interface IObserverCoverageLevel {
	/** Derived: `covered + uncovered`. The ledger reports no total of its own. */
	total: number;
	/** Measured: entities with a confirmed observer. */
	covered: number;
	/** Measured: entities still without one. */
	uncovered: number;
	/** Measured: uncovered entities that have at least one assignable applicant. */
	readyToAssign: number;
	/** Derived: `uncovered - readyToAssign`, floored at 0. Sums with `readyToAssign` to `uncovered`. */
	awaitingApplicants: number;
	/** Measured: applications received across every entity at this level — a PEOPLE count, not an entity count. */
	applicationsReceived: number;
}

export type IObserverCoverageSummary = Record<ObserverDivisionType, IObserverCoverageLevel>;

/** The five self-declared readiness facts, keyed by their field on the application DTO. */
export type ObserverQualificationKey =
	| 'hasSmartPhoneWithInternet'
	| 'isNativeSpeaker'
	| 'isAvailableAllDay'
	| 'hasPowerBank'
	| 'hasPartyAgentExperience';

export interface IObserverApplicationListParams extends IObserverManagementScopeParams {
	divisionType?: ObserverDivisionType;
	entityCode?: string;
	statusId?: number;
	/** Each one narrows to applicants who answered YES. Absent means "either answer". */
	qualifications?: ObserverQualificationKey[];
	search?: string;
	sortBy?: 'name' | 'score' | 'appliedAt';
	sortOrder?: 'asc' | 'desc';
	PageNumber?: number;
	PageSize?: number;
	ApplicationIds?: number[];
	email?: string;
}

export interface IObserverEntityListParams extends IObserverManagementScopeParams {
	divisionType: ObserverDivisionType;
	isAssigned?: boolean;
	hasApplicant?: boolean;
	code?: string;
	sortBy?: 'code' | 'applicantCount' | 'isAssigned';
	sortOrder?: 'asc' | 'desc';
	PageNumber?: number;
	PageSize?: number;
}

export interface IObserverReasonPayload {
	id: number;
	reason: string;
}

export type ObserverPagedResponse<T> = IApiResponse<T[]>;
